import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { AIScreeningService } from '../../services/ai-Screening';
import { ScreeningRoundResponseData } from '../../models/responseModels/screeningRoundDetails';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { FALLBACK_INTERVIEW_LANGUAGES, InterviewLanguage } from '@edtech-ui/constants/interview-language.constant';

@Component({
  standalone: true,
  selector: 'app-talent-acquisition',
  imports: [MatExpansionModule, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './talent-acquisition.html',
  styleUrl: './talent-acquisition.css'

})
export class TalentAcquisition {
  accessCode: string = '';
  screeningId: string = '';
  roundDetails = signal<ScreeningRoundResponseData | null>(null);
  isExpired = signal<boolean>(false);

  isB2cPractice: boolean = false;
  isStarting: boolean = false;
  showLanguageModal: boolean = false;
  selectedLanguage: string = 'en';
  languages: InterviewLanguage[] = [];


  constructor(private router: Router, private route: ActivatedRoute, private aiScreeningService: AIScreeningService, private toastr: ToastrService) { }
  ngOnInit() {
    this.accessCode = this.route.snapshot.paramMap.get('token') || '';
    let idParam = this.route.snapshot.paramMap.get('id') || '';
    
    // Defensive check: sanitize id if ?is_b2c_practice or %3F was accidentally appended to the path
    if (idParam.includes('%3F') || idParam.includes('?')) {
      const parts = idParam.split(/%3F|\?/);
      idParam = parts[0];
      const rest = parts.slice(1).join('&');
      if (rest.includes('is_b2c_practice=true') || rest.includes('is_b2c_practice%3Dtrue')) {
        this.isB2cPractice = true;
        sessionStorage.setItem('is_b2c_practice', 'true');
      }
    }
    this.screeningId = idParam;

    if (this.accessCode) {
      sessionStorage.setItem('screening_access_code', this.accessCode);
    }
    if (this.screeningId) {
      this.getScreeningDetails(this.screeningId);
    }
    this.route.queryParams.subscribe(params => {
      if (params['is_b2c_practice'] === 'true' || this.isB2cPractice) {
        this.isB2cPractice = true;
        sessionStorage.setItem('is_b2c_practice', 'true');
        if (!localStorage.getItem('token')) {
          this.router.navigate(['/login'], { queryParams: { redirectUrl: this.router.url } });
          return;
        }
        this.aiScreeningService.getLanguages().subscribe({
          next: (res: any) => {
            const data = res?.Data || res?.data;
            if (data && Array.isArray(data) && data.length > 0) {
              this.languages = data;
            }
          },
          error: (err: any) => console.warn('Pre-fetching languages failed:', err)
        });
      } else {
        sessionStorage.removeItem('is_b2c_practice');
      }
    });
  }

  getScreeningDetails(id: string) {
    this.aiScreeningService.getPublicScreeningRoundDetails(this.accessCode, id).subscribe({
      next: (response) => {
        const isSuccess = response?.success ?? (response as any)?.Success;
        const responseData = response?.data ?? (response as any)?.Data;
        const message = response?.message ?? (response as any)?.Message;

        if (isSuccess && responseData) {
          this.roundDetails.set(responseData);

          const isInvalid = responseData.round?.is_valid === false;
          const isStatusExpired = responseData.round?.status?.toLowerCase().includes('expire');
          let isTimeExpired = false;
          
          if (responseData.round?.valid_until) {
            const expiryDate = new Date(responseData.round.valid_until);
            if (new Date() > expiryDate) {
              isTimeExpired = true;
            }
          }

          if (isInvalid || isStatusExpired || isTimeExpired) {
            this.isExpired.set(true);
          }

          if (responseData.tenant?.organization_name) {
            sessionStorage.setItem('organization_name', responseData.tenant.organization_name);
          }
          if (responseData.round?.logo_url) {
            sessionStorage.setItem('logo_url', responseData.round.logo_url);
          }
          if (responseData.round) {
            sessionStorage.setItem('enable_multilanguage', responseData.round.enable_multilanguage ? 'true' : 'false');
          }
        } else {
          if (message && message.toLowerCase().includes('expire')) {
            this.isExpired.set(true);
          }
          this.toastr.error(message || 'Failed to fetch screening round details', 'Error');
        }
      },
      error: (error) => {
        const message = error?.error?.message || error?.message || '';
        if (message.toLowerCase().includes('expire')) {
          this.isExpired.set(true);
        }
        this.toastr.error(message || 'Failed to fetch screening round details', 'Error');
      }
    });
  }

  routeToVericiation() {
    if (this.isExpired()) {
      this.toastr.error('This interview link has expired. Please contact the recruiter.', 'Link Expired');
      return;
    }

    if (this.accessCode && this.screeningId) {
      const isStudentDashboard = this.route.snapshot.queryParamMap.get('isStudentDashboard');
      const queryParams = isStudentDashboard ? { isStudentDashboard: 'true' } : {};
      this.router.navigate(['/verification', this.accessCode, this.screeningId], { queryParams });
    } else {
      this.toastr.warning('Interview session information is incomplete.');
    }
  }

  onStartClick() {
    if (this.isExpired() || this.isStarting) return;

    if (this.isB2cPractice) {
      const enableMulti = this.roundDetails()?.round?.enable_multilanguage;
      if (enableMulti) {
        this.fetchLanguagesAndShowModal();
      } else {
        this.startB2cPracticeSession('en');
      }
    } else {
      this.routeToVericiation();
    }
  }

  fetchLanguagesAndShowModal() {
    if (this.languages && this.languages.length > 0) {
      this.showLanguageModal = true;
      return;
    }

    this.isStarting = true;
    this.aiScreeningService.getLanguages().subscribe({
      next: (res: any) => {
        this.isStarting = false;
        const data = res?.Data || res?.data;
        if (data && Array.isArray(data) && data.length > 0) {
          this.languages = data;
        } else {
          this.languages = this.getFallbackLanguages();
        }
        this.showLanguageModal = true;
      },
      error: (err: any) => {
        this.isStarting = false;
        console.error('Error fetching languages from API:', err);
        this.languages = this.getFallbackLanguages();
        this.showLanguageModal = true;
      }
    });
  }

  private getFallbackLanguages(): InterviewLanguage[] {
    return [...FALLBACK_INTERVIEW_LANGUAGES];
  }

  selectLanguage(code: string) {
    this.selectedLanguage = code;
  }

  confirmLanguageSelection() {
    this.showLanguageModal = false;
    sessionStorage.setItem('preferred_interview_language', this.selectedLanguage);
    this.startB2cPracticeSession(this.selectedLanguage);
  }

  startB2cPracticeSession(language: string) {
    this.isStarting = true;
    sessionStorage.setItem('preferred_interview_language', language);
    this.aiScreeningService.startPracticeSession(this.screeningId, language).subscribe({
      next: (res: any) => {
        this.isStarting = false;
        if (res && res.Success && res.Data) {
          const { session_id, candidate_id, token, candidate_name, candidate_email, candidate_phone } = res.Data;
          if (token) {
            sessionStorage.setItem('candidate_token', token);
          }
          const candidateInfo = { name: candidate_name || 'Candidate', email: candidate_email || '', phone: candidate_phone || '' };
          sessionStorage.setItem('candidate_info', JSON.stringify(candidateInfo));
          this.router.navigate(['/interview-session', candidate_id, session_id, this.screeningId], {
            queryParams: { is_b2c_practice: 'true', lang: language }
          });
        } else {
          this.toastr.error(res?.Message || 'Failed to start practice session');
        }
      },
      error: (err: any) => {
        this.isStarting = false;
        this.toastr.error(err?.error?.Message || 'An error occurred while starting the session');
      }
    });
  }

  get companyDescription(): string {
    const data = this.roundDetails()?.tenant;
    return data?.description || (data?.organization_name ? `${data.organization_name} is committed to building the future of recruitment through AI-powered experiences.` : '');
  }
}


